/* Step 2 — AI Analysis. Ported from the prototype's analysisBody(), now driven by
   the real backend. Starting analysis POSTs .../analysis/run; progress arrives over
   sockets (deal:analysis:started/progress/completed/failed). On completion the report
   is refetched from GET .../analysis: a 0–100 score with slide-cited strengths/risks,
   a deal snapshot and deck-completeness checks. No frontend timers fake the progress. */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import socket from '../../../../../config';
import Bubble from '../../components/Bubble';
import { Loading, ErrorState } from '../../components/States';
import { useToast } from '../../components/Toast';
import { useDealState } from '../../state';
import { BASE, initialsOf } from '../../helpers';
import { PROGRESS_TASKS } from '../../data/config';
import { fetchAnalysis, runAnalysis as apiRunAnalysis, exportPdf, ok } from '../../api';

const RING_C = 2 * Math.PI * 48;

function AnalysisProgress({ deal, progress }) {
	const showToast = useToast();
	const pct = progress.total ? Math.round((progress.current / progress.total) * 100) : 0;
	const doneCount = Math.floor((pct / 100) * PROGRESS_TASKS.length);
	const secs = Math.max(0, Math.round((100 - pct) / 2));

	return (
		<main className="page screen">
			<div className="dealstrip">
				<span className="dealstrip__logo">{initialsOf(deal.name)}</span>
				<div>
					<div className="dealstrip__name">{deal.name}</div>
					<div className="dealstrip__meta">{(deal.detail && deal.detail.round) || 'Seed'} · {deal.sector} · pitch deck</div>
				</div>
				<span className="chip chip--blue" style={{ marginLeft: 'auto' }}>Analysing</span>
			</div>
			<div className="card" style={{ marginTop: 14, padding: '28px 26px' }}>
				<div style={{ display: 'flex', gap: 16, maxWidth: 640, margin: '0 auto' }}>
					<div className="aiavatar"><span>AI</span></div>
					<Bubble><strong>Hi, I'm Intelligentsia.</strong> I'm reading {deal.name.split(' ')[0]}'s deck now — extracting the numbers, checking the market maths and flagging anything the committee should see.</Bubble>
				</div>
				<div style={{ maxWidth: 576, margin: '22px auto 0' }}>
					<div className="progress"><div className="progress__bar" style={{ width: `${pct}%` }} /></div>
					<div className="progress__meta">{pct === 0 ? 'Starting…' : `${pct}% · ${progress.label || 'working'}`}</div>
					<div className="tasklist">
						{PROGRESS_TASKS.map((t, i) => (
							<div key={i} className={`task ${i < doneCount ? 'is-done' : ''} ${i === doneCount ? 'is-active' : ''}`}>
								<span className="task__dot">✓</span>{t}
							</div>
						))}
					</div>
					<div style={{ textAlign: 'center', marginTop: 18 }}>
						<button className="pill pill--outline" onClick={() => showToast("We'll notify you the moment the report is ready")}>Notify me when it's ready</button>
					</div>
				</div>
			</div>
		</main>
	);
}

function AnalysisReport({ deal, A }) {
	const navigate = useNavigate();
	const showToast = useToast();
	const [offset, setOffset] = useState(RING_C);
	const [exporting, setExporting] = useState(false);

	useEffect(() => {
		const t = setTimeout(() => setOffset(RING_C * (1 - (A.score || 0) / 100)), 80);
		return () => clearTimeout(t);
	}, [A.score]);

	const compColor = c => (c.state === 'miss' ? 'var(--dlg-red)' : 'var(--dlg-blue)');
	const compMark = c => (c.state === 'ok'
		? <span style={{ color: 'var(--dlg-green-ink)' }}>✓</span>
		: c.state === 'part' ? '◐' : <span style={{ color: 'var(--dlg-red-ink)' }}>✕</span>);

	const chips = A.chips || [];
	const strengths = A.strengths || [];
	const risks = A.risks || [];
	const snapshot = A.snapshot || [];
	const completeness = A.completeness || [];

	const download = async () => {
		if (exporting) return;
		setExporting(true);
		const res = await exportPdf(deal.id, 'analysis');
		setExporting(false);
		if (ok(res) && res.body && res.body.url) { window.open(res.body.url, '_blank', 'noopener'); showToast('Report PDF ready'); }
		else showToast((res && res.message) || 'Export is unavailable right now');
	};

	return (
		<main className="page screen">
			<div className="dealstrip">
				<span className="dealstrip__logo">{initialsOf(deal.name)}</span>
				<div>
					<div className="dealstrip__name">{deal.name}</div>
					<div className="dealstrip__meta">{(deal.detail && deal.detail.round) || 'Seed'} · {deal.sector}</div>
				</div>
				<span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
					{chips.map((c, i) => <span key={i} className={`chip ${c.cls}`}>{c.text}</span>)}
				</span>
			</div>

			<div className="card" style={{ marginTop: 14, display: 'flex', gap: 22, alignItems: 'center' }}>
				<div className="ring">
					<svg width="108" height="108" viewBox="0 0 108 108">
						<circle className="ring__track" cx="54" cy="54" r="48" />
						<circle className="ring__val" cx="54" cy="54" r="48" strokeDasharray={RING_C} strokeDashoffset={offset} />
					</svg>
					<div className="ring__num">{A.score}<small>/ 100</small></div>
				</div>
				<div>
					<div style={{ fontSize: 17, fontWeight: 600, color: 'var(--dlg-pos)' }}>{A.recommend}</div>
					<div style={{ fontSize: 14, color: 'var(--dlg-ink-soft)', marginTop: 4, maxWidth: 620 }}>{A.summary}</div>
					<span className="chip chip--blue" style={{ marginTop: 10 }}>✦ Analysed by Intelligentsia · findings cite their slide</span>
				</div>
			</div>

			<div className="grid grid--split45" style={{ marginTop: 12 }}>
				<div className="card">
					<div className="card__label" style={{ marginBottom: 8 }}>Strengths</div>
					{strengths.map((s, i) => (
						<div key={i} className="finding"><span className="finding__dot" style={{ background: 'var(--dlg-green)' }} />
							<span>{s.text}{s.slide ? <span className="cite"> slide {s.slide}</span> : null}</span></div>
					))}
				</div>
				<div className="card">
					<div className="card__label" style={{ marginBottom: 8 }}>Risks &amp; red flags</div>
					{risks.map((r, i) => (
						<div key={i} className="finding"><span className="finding__dot" style={{ background: r.level === 'red' ? 'var(--dlg-red)' : 'var(--dlg-amber)' }} />
							<span>{r.text}{r.slide ? <span className="cite"> slide {r.slide}</span> : null}</span></div>
					))}
				</div>
			</div>

			<div className="grid grid--split45">
				<div className="card">
					<div className="card__label" style={{ marginBottom: 8 }}>Deal snapshot</div>
					<dl className="snap">
						{snapshot.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
					</dl>
				</div>
				<div className="card">
					<div className="card__label" style={{ marginBottom: 8 }}>Deck completeness{A.completenessLabel ? ` · ${A.completenessLabel}` : ''}</div>
					{completeness.map(c => (
						<div key={c.name} className="comp">
							<span className="comp__name">{c.name}</span>
							<div className="comp__bar"><div className="comp__fill" style={{ width: `${c.pct}%`, background: compColor(c) }} /></div>
							<span className="comp__mark">{compMark(c)}</span>
						</div>
					))}
					{A.missingNote ? <div style={{ fontSize: 12.5, color: 'var(--dlg-muted)', marginTop: 8 }}>{A.missingNote}</div> : null}
				</div>
			</div>

			<div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
				<button className="pill pill--primary" onClick={() => navigate(`${BASE}/deal/${deal.id}/triage`)}>Proceed to triage →</button>
				<button className="pill pill--outline" onClick={download} disabled={exporting}>{exporting ? 'Exporting…' : 'Download report'}</button>
			</div>
		</main>
	);
}

export default function Analysis({ deal }) {
	const { markAnalysisDone } = useDealState();
	const [phase, setPhase] = useState('loading');       // loading | running | report | error
	const [analysis, setAnalysis] = useState(null);
	const [progress, setProgress] = useState({ current: 0, total: 4, label: '' });
	const startedRef = useRef(false);

	const loadReport = useCallback(async () => {
		const a = await fetchAnalysis(deal.id);
		if (a && a.status === 'complete') { setAnalysis(a); setPhase('report'); markAnalysisDone(deal.id); return 'complete'; }
		return a ? a.status : null;
	}, [deal.id, markAnalysisDone]);

	// Decide initial phase and (if needed) kick off a run.
	useEffect(() => {
		let cancelled = false;
		(async () => {
			const a = await fetchAnalysis(deal.id);
			if (cancelled) return;
			if (a && a.status === 'complete') { setAnalysis(a); setPhase('report'); markAnalysisDone(deal.id); return; }
			setPhase('running');
			// Start a run only if none is already in flight (avoid double-charging on refresh).
			if ((!a || a.status === 'failed') && !startedRef.current) {
				startedRef.current = true;
				const res = await apiRunAnalysis(deal.id);
				if (!ok(res)) { if (!cancelled) setPhase('error'); }
			}
		})();
		return () => { cancelled = true; };
	}, [deal.id, markAnalysisDone]);

	// Socket progress — scoped to this deal; listeners cleaned up on unmount.
	useEffect(() => {
		const dealId = String(deal.id);
		const onStarted = p => { if (String(p.dealId) === dealId) setProgress({ current: 0, total: p.total || 4, label: 'Reading the deck' }); };
		const onProgress = p => { if (String(p.dealId) === dealId) setProgress({ current: p.current || 0, total: p.total || 4, label: p.label || '' }); };
		const onCompleted = async p => { if (String(p.dealId) === dealId) await loadReport(); };
		const onFailed = p => { if (String(p.dealId) === dealId) setPhase('error'); };

		socket.on('deal:analysis:started', onStarted);
		socket.on('deal:analysis:progress', onProgress);
		socket.on('deal:analysis:completed', onCompleted);
		socket.on('deal:analysis:failed', onFailed);
		return () => {
			socket.off('deal:analysis:started', onStarted);
			socket.off('deal:analysis:progress', onProgress);
			socket.off('deal:analysis:completed', onCompleted);
			socket.off('deal:analysis:failed', onFailed);
		};
	}, [deal.id, loadReport]);

	if (phase === 'loading') return <main className="page screen"><Loading label="Loading analysis…" /></main>;
	if (phase === 'error') return <main className="page screen"><ErrorState title="Analysis could not be completed" sub="The run failed or the deck could not be read. You can retry." onRetry={() => { startedRef.current = false; apiRunAnalysis(deal.id).then(() => setPhase('running')); }} /></main>;
	if (phase === 'report' && analysis) return <AnalysisReport deal={deal} A={analysis} />;
	return <AnalysisProgress deal={deal} progress={progress} />;
}
